/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  BuilderNavigationService,
  BuilderUserContext,
  FilterNames,
  FilterRestService,
  HlpUpdateListingStatusDialogConfig,
  hlpUpdateListingStatusDialogRoute,
} from '@gh/builder-shared';
import {
  AppRouter,
  DateTimeTypes,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData,
  IS_REGION_AU,
  NumberTypes,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource, SortDirection } from '@gh/core-data';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildDateTimeColumn,
  buildNumberColumnLeft,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { TranslationService } from '@gh/core-mls';
import { HlpListBusinessService } from '../../resources/hlp-list-business.service';
import {
  HlpListItem,
  isSalespersonSelectItemInactive,
  SalespersonSelectItem,
} from '../../resources/hlp.model';
import {
  buildHlpBrochureLink,
  BuilderPermission,
  HlpStatus,
  ListingStatus,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import { filter$, map$ } from '@gh/rx/operators';
import { MessageBasket } from '@gh/core-messages';
import { omit } from 'lodash';
import { Office } from '../../../builder-shared/switch-to/resources/office.model';
import { of$ } from '@gh/rx';
import { MasterZone } from '../../../builder-shared/switch-to/resources/master-zone.model';

@Component({
  standalone: false,
  selector:    'gh-hlp-list',
  styleUrls:   ['hlp-list.component.scss'],
  templateUrl: 'hlp-list.component.html',
})
export class HlpListComponent implements OnInit {
  dataSource: SlicedDataSource<any>;
  form: FilterableFormState<any>;
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor({ page: 0, pageSize: -1 , sort: { field: 'updatedDate', direction: SortDirection.Descending }});
  masterZoneList: MasterZone[] = [];
  filter: any;
  isAuRegion = IS_REGION_AU;

  get hasEditViewPermission(): boolean {
    return this.userContext.hasPermission(BuilderPermission.Sales.Hlp.EDIT)
      || this.userContext.hasPermission(BuilderPermission.Sales.Hlp.VIEW);
  }

  constructor(
    private route: ActivatedRoute,
    private routeData: RouteDataService,
    private userContext: BuilderUserContext,
    private formStateBuilder: FormStateBuilder,
    private businessService: HlpListBusinessService,
    private notifications: NotificationService,
    private router: AppRouter,
    private angRouter: Router,
    private dialogService: DialogService,
    private translations: TranslationService,
    private dialogs: DialogService,
    private filterService: FilterRestService,
    private waitingService: WaitingService,
    private navigationService: BuilderNavigationService,
    public gridDefinitionFactory: GridDefinitionFactory,
  ) {
    this.routeData.mergeQueryParams({ sortDirection: 'desc', sortField: 'updatedDate'});
  }

  ngOnInit(): void {
    const {filter: initFilter, gridLayout, masterZones, offices, salesPerson } = getRouteInitData(this.route);
    const office = initFilter.officeId
      ? offices.find((item: Office) => item.id === initFilter.officeId)
      : {id: '', name: 'All'};
    const masterZone = initFilter.masterZoneId
      ? masterZones.find((item: Office) => item.id === initFilter.masterZoneId)
      : {id: '', name: 'All'};
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        updatedDate: {
          ...buildDateTimeColumn(DateTimeTypes.DateTime),
          sort: 'desc',
        },
        addedDate:   buildDateTimeColumn(DateTimeTypes.DateTime),
        displayPrice: buildNumberColumnLeft(NumberTypes.Price),
        floorArea: buildNumberColumnLeft(NumberTypes.PriceWithoutDecimals),
        status:      {
          minWidth: 95,
          cellClass: ({ data }: { data: any }) => (
            {
              [HlpStatus.Deleted]:       'cell--negative',
              [HlpStatus.Active]:        'cell--positive',
              [HlpStatus.Draft]:         'cell--unimportant',
            }[data.statusValue]
          ),
        },
      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        getRowId: (params) => String(params.data.id)
      },
    );
    this.gridDefinition.bind(gridLayout);
    this.masterZoneList = masterZones.sort((a: MasterZone, b: MasterZone) => {
      if ((!a.active && !b.active) || (a.active && b.active)) {
        return 0;
      } else if (a.active && !b.active) {
        return  -1;
      }
      return 1;
    });
    this.form = filterableForm(this.formStateBuilder.buildPlain({
                                                                  value:    {
                                                                    ...initFilter,
                                                                    salesPerson,
                                                                    office,
                                                                    masterZone,
                                                                  },
                                                                  children: {
                                                                    masterZoneId:  { visible: this.userContext.corporateView },
                                                                    masterZone: { visible: this.userContext.corporateView },
                                                                    officeId:      void 0,
                                                                    office:        void 0,
                                                                    searchString:  void 0,
                                                                    salesPersonId: void 0,
                                                                    searchInSiteAddress: void 0,
                                                                    showPublished: void 0,
                                                                    showDraft: void 0,
                                                                    showDeleted: void 0,
                                                                    salesPerson: void 0,
                                                                  },
                                                                }));
    let isInit = true;
    const tracker = this.routeData.tracker(filterTracker(this.form)).withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.filter = {
        ...omit(filter,
                'salesPerson',
                'salesPersonId',
                'office',
                'officeId',
                'masterZone',
                'masterZoneId',
                ),
        searchInSiteAddress: true,
      };
      if (filter.salesPerson?.id) {
        this.filter.salesPersonId = filter.salesPerson.id;
      }
      if (filter.office?.id) {
        this.filter.officeId = filter.office.id;
      }
      if (filter.masterZone?.id) {
        this.filter.masterZoneId = filter.masterZone.id;
      }
      this.dataSource = this.businessService.findAllAsDataSource(this.filter);
      if (!isInit) {
        this.filterService.update(
              this.userContext.corporateView ? FilterNames.hlpListCorp : FilterNames.hlpListMaster,
              this.filter,
            ).subscribe();
          }
      isInit = false;
    });
  }

  isLinkVisible(hlp: HlpListItem): boolean {
    return hlp.status === HlpStatus.Active && !!hlp.websiteLink;
  }

  getBrochureLink(hlp: HlpListItem): string {
    return buildHlpBrochureLink(hlp.websiteLink);
  }

  goToAel(hlp?: HlpListItem): void {
    if (hlp) {
      const navigationParams = {
        queryParams: {
          label:      'lbl_hlps',
          entityType: 'HLP',
          entityId: hlp.id,
        },
      };
      const url = this.angRouter.serializeUrl(
        this.angRouter.createUrlTree([`office-view/${ hlp.officeId }/ael`], navigationParams));
      window.open(url, '_blank');
      return;
    }
    this.navigationService.goToAel({
      label:      'lbl_hlps',
      entityType: 'HLP',
      eventTypeIds: [266, 267, 268, 269, 270, 271, 272],
    });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onSelectOffice(): void {
    this.resetSalesPerson();
    this.form.emitFilter();
  }

  onSelectMasterZone(value: string): void {
    if (value) {
      this.resetOffice();
      this.form.emitFilter();
    } else {
      this.resetOffice();
      this.form.emitFilter();
    }
  }

  onReset(): void {
    this.form.emitFilter();
  }

  goToHlpDetails(hlp: HlpListItem): void {
    if (!this.hasEditViewPermission) {
      return;
    }
    const url = this.angRouter.serializeUrl(
      this.angRouter.createUrlTree([`office-view/${ hlp.officeId }/sales/hlp/${ hlp.id }`]));
    window.open(url, '_blank');
  }

  loadSalesPersons(event: AutocompleteLoadMoreEvent): void {
    event.load(this.businessService.findSalesPersonsByName(event.query, {
      masterZoneIds: this.form.get('masterZoneId').value ? [this.form.get('masterZoneId').value] : [],
      officeIds:     this.form.get('officeId').value ? [this.form.get('officeId').value] : [],
      showInactive:  true,
    }).pipe(map$((res) => [{id: '', name: 'All'}, ...res])));
  }

  loadMasterZones(event: AutocompleteLoadMoreEvent): void {
    event.load(of$(event.query.filter
    ? this.masterZoneList.filter((masterZone: MasterZone) => masterZone.name.toLowerCase().includes(event.query.filter.toLowerCase()))
    : [{id: '', name: 'All'}, ...this.masterZoneList]));
  }

  loadOffice(event: AutocompleteLoadMoreEvent): void {
    let masterZoneId;
    if (this.userContext.corporateView) {
      masterZoneId = this.form.get('masterZoneId').value;
    } else if (this.userContext.masterView) {
      masterZoneId = this.userContext.masterZone?.id;
    }
    if (!masterZoneId) {
      event.load(of$([{id: '', name: 'All'}]));
      return;
    }
    event.load(this.businessService.findOfficesByMasterName(masterZoneId, event.query.filter));
  }

  onSalesPersonSelected(salesPerson: SalespersonSelectItem): void {
    if (!salesPerson || salesPerson.id !== this.form.getControl('salesPersonId').value) {
      this.form.getControl('salesPersonId').setValue(salesPerson?.id || '');
      this.form.emitFilter();
    }
  }

  onOfficeSelected(office: Office): void {
    if (!office || office.id !== this.form.getControl('officeId').value) {
      this.form.getControl('officeId').setValue(office?.id || '');
      this.resetSalesPerson();
    }
  }

  onMasterZoneSelected(masterZone: MasterZone): void {
    if (!masterZone || masterZone.id !== this.form.getControl('masterZoneId').value) {
      this.form.getControl('masterZoneId').setValue(masterZone?.id || '');
      this.resetOffice();
    }
  }

  isDeleteShow(hlp: HlpListItem): boolean {
    return (
        this.userContext.hasPermission(BuilderPermission.Sales.Hlp.DELETE)
        || this.userContext.hasSystemRole(SystemRoleBit.Master)
        || this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      )
      && hlp.statusValue !== HlpStatus.Deleted;
  }

  onDeleteClick(hlp: HlpListItem): void {
    this.dialogService.confirmation(
      yesNoConfirmation('msg_hlp_deletion')).pipe(
      filter$(yesAnswer)).subscribe(() => {
      if (hlp.statusValue === HlpStatus.Active) {
        this.businessService.getHlpInfo(hlp.id).subscribe((hlpDetails) => {
          if (hlpDetails.publishedOnDomainComAu || hlpDetails.publishedOnRealestateComAu) {
            this.dialogService.openWithInput(hlpUpdateListingStatusDialogRoute, <HlpUpdateListingStatusDialogConfig>{
                hlp: hlpDetails,
              },
                                             {
                autoFocus: false,
              },
            ).afterClosed().pipe(
              filter$(Boolean))
              .subscribe(({ continueDeleting, listingStatus }) => {
                if (continueDeleting) {
                  this.deleteHlp(hlp, listingStatus);
                }
              });
            return;
          }
          this.deleteHlp(hlp);
        },
                                                          this.notifications.processAll);
        return;
      }
      this.deleteHlp(hlp);
    });
  }
  canBeEditable(hlp: HlpListItem): boolean {
    return hlp.status !== HlpStatus.Deleted;
  }
  isMZInactive(mz: MasterZone): boolean {
    return Object.prototype.hasOwnProperty.call(mz, 'active') && !mz.active;
  }
  isOfficeDeactivated(office: any): boolean {
    return !!office.status && office.status.toUpperCase() === 'DEACTIVATED';
  }

  isSalespersonInactive(salesperson: SalespersonSelectItem): boolean {
    return isSalespersonSelectItemInactive(salesperson);
  }

  onClone(hlp: HlpListItem): void {
    const url = this.angRouter.serializeUrl(
      this.angRouter.createUrlTree([`office-view/${ hlp.officeId }/sales/hlp`], {queryParams: {
          clonedHlp: hlp.id,
        }}));
    window.open(url, '_blank');
  }

  onCreateQuote(hlp: HlpListItem): void {
    const url = this.angRouter.serializeUrl(
      this.angRouter.createUrlTree([`office-view/${ hlp.officeId }/sales/hlp`], {queryParams: {
        createLeadQuote: hlp.id,
        }}));
    window.open(url, '_blank');
  }

  private resetOffice(): void {
    this.form.getControl('officeId').setValue('');
    this.form.getControl('office').setValue({ id: '', name: 'All' });
    this.resetSalesPerson();
  }

  private resetSalesPerson(): void {
    this.form.getControl('salesPersonId').setValue('');
    this.form.getControl('salesPerson').setValue({ id: '', name: 'All' });
    this.form.emitFilter();
  }

  private deleteHlp(hlp: HlpListItem, listingStatus?: ListingStatus): void {
      this.businessService
        .delete(hlp.id, listingStatus).pipe(
        this.waitingService.operator()).subscribe(
        () => {
          this.dataSource = this.businessService.findAllAsDataSource(this.filter);
        },
        (error: MessageBasket) => this.notifications.processAll(error),
      );
  }
}
